import React, { ReactElement, useEffect } from "react";
import { useCombobox } from "downshift";
import cx from "classnames";
import { Transition } from "@headlessui/react";
import { CheckIcon, ChevronIcon } from "../../atoms/Icons";
import Divider from "../../atoms/Divider";
import * as inputStyles from "../../shared/inputStyles";
import type { SelectItem } from "../Select";

export interface Props {
  disabled?: boolean;
  variant?: "dark" | "light";
  hasError?: boolean;
  helperText?: string;
  label?: string | ReactElement;
  placeholder?: string;
  onInputChange?: (value?: string, selectedItem?: SelectItem | null) => void;
  onStateChange: (state: any) => void;
  iconLeft?: ReactElement;
  items: SelectItem[];
  initialSelectedValue?: string | number | undefined;
  required?: boolean;
  showToggle?: boolean;
  showRequiredLabel?: boolean;
  enableSections?: boolean;
}

export default function AutocompleteField({
  disabled = false,
  variant = "light",
  hasError,
  helperText,
  label,
  placeholder,
  onInputChange,
  onStateChange,
  iconLeft,
  items,
  initialSelectedValue,
  required = false,
  showToggle = true,
  showRequiredLabel = true,
  enableSections = false,
}: Props): ReactElement {
  const selectableItems = enableSections
    ? items.filter((item) => !item.isSection)
    : items;

  const preSelectedItem = selectableItems.find(
    (item) => item.value === initialSelectedValue,
  );

  const isDark = variant === "dark";

  const {
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    getToggleButtonProps,
    isOpen,
    selectedItem,
    highlightedIndex,
  } = useCombobox({
    onInputValueChange({ inputValue, selectedItem }) {
      onInputChange?.(inputValue, selectedItem);
    },
    onStateChange({ selectedItem }) {
      if (!selectedItem || (enableSections && selectedItem.isSection)) {
        return;
      }
      onStateChange(selectedItem);
    },
    items: selectableItems,
    itemToString: (item) => (item ? `${item.label}` : ""),
    initialSelectedItem: preSelectedItem,
  });

  useEffect(() => {
    if (preSelectedItem) {
      onStateChange(preSelectedItem);
    }
  }, []);

  return (
    <div className={inputStyles.inputGroup}>
      <div className="w-full">
        {label && (
          <label
            {...getLabelProps()}
            className={cx(inputStyles.label, {
              [inputStyles.labelDisabled]: disabled,
              [inputStyles.labelDark]: variant === "dark",
            })}
          >
            {label}
            {required && showRequiredLabel && (
              <span
                className={cx(inputStyles.labelRequired, {
                  [inputStyles.labelRequiredDark]: variant === "dark",
                })}
              >
                *
              </span>
            )}
          </label>
        )}
        <div className="relative flex w-full flex-row">
          {iconLeft && <div className="absolute left-4 top-3">{iconLeft}</div>}
          <input
            {...getInputProps()}
            autoComplete="off"
            className={cx(
              variant === "dark" ? inputStyles.inputDark : inputStyles.input,
              {
                [inputStyles.inputError]: hasError,
                [inputStyles.selectButtonDisabled]: disabled,
                "pl-12": iconLeft,
              },
            )}
            placeholder={placeholder}
            tabIndex={0}
          />
          {showToggle && (
            <div
              {...getToggleButtonProps()}
              aria-label="toggle menu"
              className="absolute bottom-3 right-4"
            >
              <ChevronIcon
                direction="down"
                className={cx({
                  "-rotate-180 transform transition-all": isOpen,
                  "rotate-0 transform transition-all": !isOpen,
                  "text-white": variant === "dark",
                })}
              />
            </div>
          )}
        </div>
      </div>
      <div className="relative z-20" {...getMenuProps({})}>
        <Transition
          as="div"
          show={isOpen && Boolean(items.length)}
          {...inputStyles.transitionProps}
        >
          <ul
            className={cx(
              isDark ? inputStyles.selectMenuDark : inputStyles.selectMenu,
              enableSections && "max-h-96 overflow-auto",
            )}
          >
            {items.map((item, index) => {
              const isSelected = selectedItem?.value === item.value;
              const isSection = enableSections && item.isSection;

              const selectableIndex =
                enableSections && !isSection
                  ? selectableItems.findIndex(
                      (selectableItem) => selectableItem.value === item.value,
                    )
                  : index;

              if (isSection) {
                const isMainSection = item.value === "__section_all__";

                return (
                  <React.Fragment key={item.value}>
                    {isMainSection && <Divider variant="dark" margin="sm" />}
                    <li
                      className={cx("px-4 pb-2 text-sm", {
                        "pt-4": !isMainSection,
                        "pt-0": isMainSection,
                        "text-neutral-400 bg-neutral-800": isDark,
                        "text-neutral-600 bg-neutral-100": !isDark,
                      })}
                    >
                      {item.label}
                    </li>
                  </React.Fragment>
                );
              }

              const isHighlighted = highlightedIndex === selectableIndex;

              return (
                <li
                  key={item.value}
                  {...getItemProps({ item, index: selectableIndex })}
                  className={cx(
                    isDark
                      ? inputStyles.selectMenuItemDark
                      : inputStyles.selectMenuItem,
                    {
                      "bg-neutral-700": isDark && (isSelected || isHighlighted),
                      "bg-neutral-50": !isDark && (isSelected || isHighlighted),
                    },
                  )}
                >
                  <div className="flex flex-row gap-3 items-center">
                    {selectedItem && (
                      <CheckIcon
                        size={24}
                        className={isSelected ? "" : "invisible"}
                      />
                    )}
                    {item.icon && <div>{item.icon}</div>}
                    {item.label}
                  </div>
                </li>
              );
            })}
          </ul>
        </Transition>
      </div>
      {helperText && (
        <span
          id="helperText"
          className={cx(inputStyles.helperText, {
            [inputStyles.helperTextDisabled]: disabled,
            [inputStyles.helperTextDark]: variant === "dark",
          })}
        >
          {helperText}
        </span>
      )}
    </div>
  );
}
